import React, { useState, useEffect, useCallback } from "react";
import axios from "axios";
import { Smartphone, RefreshCw, CheckCircle, AlertCircle, Clock, ChevronDown, ChevronUp, Wifi, Database, Activity } from "lucide-react";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { toast } from "sonner";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const JOURS_FR = ["Dim", "Lun", "Mar", "Mer", "Jeu", "Ven", "Sam"];

function formatDate(iso) {
  if (!iso) return "—";
  const d = new Date(iso);
  return d.toLocaleDateString("fr-FR", { day: "2-digit", month: "short", year: "numeric" });
}

function formatTime(iso) {
  if (!iso) return "—";
  const d = new Date(iso);
  return d.toLocaleTimeString("fr-FR", { hour: "2-digit", minute: "2-digit", second: "2-digit" });
}

function timeAgo(iso) {
  if (!iso) return "";
  const diff = Math.floor((Date.now() - new Date(iso).getTime()) / 1000);
  if (diff < 60) return `il y a ${diff}s`;
  if (diff < 3600) return `il y a ${Math.floor(diff / 60)}min`;
  if (diff < 86400) return `il y a ${Math.floor(diff / 3600)}h`;
  return `il y a ${Math.floor(diff / 86400)}j`;
}

function StatusBadge({ status }) {
  const map = {
    created:    { label: "Créé",      bg: "bg-green-100",  text: "text-green-700",  dot: "bg-green-500"  },
    duplicate:  { label: "Doublon",   bg: "bg-yellow-100", text: "text-yellow-700", dot: "bg-yellow-500" },
    not_found:  { label: "Inconnu",   bg: "bg-red-100",    text: "text-red-700",    dot: "bg-red-500"    },
    error:      { label: "Erreur",    bg: "bg-red-100",    text: "text-red-700",    dot: "bg-red-500"    },
  };
  const s = map[status] || { label: status, bg: "bg-slate-100", text: "text-slate-700", dot: "bg-slate-400" };
  return (
    <span className={`inline-flex items-center gap-1.5 px-2 py-0.5 rounded-full text-xs font-semibold ${s.bg} ${s.text}`}>
      <span className={`h-1.5 w-1.5 rounded-full ${s.dot}`}></span>
      {s.label}
    </span>
  );
}

function SyncLogCard({ log }) {
  const [expanded, setExpanded] = useState(false);
  const hasErrors  = log.errors > 0;
  const allCreated = log.created === log.total_sent && log.errors === 0;

  return (
    <div className={`border rounded-xl overflow-hidden transition-all ${allCreated ? "border-green-200 bg-green-50/30" : hasErrors ? "border-red-200 bg-red-50/20" : "border-slate-200 bg-white"}`}>
      {/* Header de la carte */}
      <button className="w-full text-left px-5 py-4 flex items-start gap-4" onClick={() => setExpanded(!expanded)}>
        {/* Icône */}
        <div className={`mt-0.5 flex-shrink-0 h-10 w-10 rounded-xl flex items-center justify-center shadow-sm ${allCreated ? "bg-green-500" : hasErrors ? "bg-red-500" : "bg-orange-500"}`}>
          <Smartphone className="h-5 w-5 text-white" />
        </div>

        {/* Infos principales */}
        <div className="flex-1 min-w-0">
          <div className="flex items-center gap-2 flex-wrap">
            <span className="font-semibold text-slate-900 text-sm truncate">
              {log.operator && log.operator !== "Application mobile" ? (
                <><span className="text-slate-500 font-normal">Synchronisé par : </span>{log.operator}</>
              ) : (
                <span className="text-slate-500 font-normal italic">Application mobile</span>
              )}
            </span>
            <span className="text-xs text-slate-400">{timeAgo(log.synced_at)}</span>
          </div>
          <div className="mt-1 flex items-center gap-3 flex-wrap text-xs text-slate-600">
            <span className="flex items-center gap-1">
              <Clock className="h-3.5 w-3.5 text-slate-400" />
              {formatDate(log.synced_at)} à {formatTime(log.synced_at)}
            </span>
            <span className="flex items-center gap-1">
              <Database className="h-3.5 w-3.5 text-slate-400" />
              {log.total_sent} enregistrement{log.total_sent > 1 ? "s" : ""} envoyés
            </span>
          </div>
          {/* Barre de résultat */}
          <div className="mt-2 flex items-center gap-2 flex-wrap">
            {log.created > 0 && (
              <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-xs font-semibold bg-green-100 text-green-700">
                <CheckCircle className="h-3 w-3" /> {log.created} créé{log.created > 1 ? "s" : ""}
              </span>
            )}
            {log.duplicates > 0 && (
              <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-xs font-semibold bg-yellow-100 text-yellow-700">
                <AlertCircle className="h-3 w-3" /> {log.duplicates} doublon{log.duplicates > 1 ? "s" : ""}
              </span>
            )}
            {log.errors > 0 && (
              <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-xs font-semibold bg-red-100 text-red-700">
                <AlertCircle className="h-3 w-3" /> {log.errors} erreur{log.errors > 1 ? "s" : ""}
              </span>
            )}
          </div>
        </div>

        {/* Chevron */}
        <div className="flex-shrink-0 mt-2 text-slate-400">
          {expanded ? <ChevronUp className="h-4 w-4" /> : <ChevronDown className="h-4 w-4" />}
        </div>
      </button>

      {/* Détail des enregistrements */}
      {expanded && log.records && log.records.length > 0 && (
        <div className="border-t border-slate-100 px-5 py-3">
          <p className="text-xs font-semibold text-slate-500 uppercase tracking-wide mb-2">Détail des scans synchronisés</p>
          <div className="space-y-1.5">
            {log.records.map((rec, i) => (
              <div key={i} className="flex items-center justify-between text-xs bg-white rounded-lg px-3 py-2 border border-slate-100">
                <div className="flex items-center gap-3">
                  <span className="font-mono font-semibold text-slate-700">{rec.matricule}</span>
                  {rec.worker_name && <span className="text-slate-500">{rec.worker_name}</span>}
                </div>
                <div className="flex items-center gap-3 text-slate-500">
                  <span>{rec.date}</span>
                  <span>{rec.time ? rec.time.slice(0, 5) : "—"}</span>
                  <StatusBadge status={rec.status} />
                </div>
              </div>
            ))}
          </div>
        </div>
      )}
      {expanded && (!log.records || log.records.length === 0) && (
        <div className="border-t border-slate-100 px-5 py-3 text-xs text-slate-400 text-center">
          Aucun détail disponible pour cette sync
        </div>
      )}
    </div>
  );
}

export default function SyncMonitor() {
  const [logs, setLogs]       = useState([]);
  const [stats, setStats]     = useState(null);
  const [loading, setLoading] = useState(true);
  const [lastRefresh, setLastRefresh] = useState(null);
  const [autoRefresh, setAutoRefresh] = useState(true);

  const fetchData = useCallback(async () => {
    try {
      const [logsRes, statsRes] = await Promise.all([
        axios.get(`${API}/sync/logs?limit=100`),
        axios.get(`${API}/sync/stats`),
      ]);
      setLogs(logsRes.data);
      setStats(statsRes.data);
      setLastRefresh(new Date());
      setLoading(false);
    } catch (err) {
      toast.error("Erreur de chargement des synchros");
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchData();
  }, [fetchData]);

  useEffect(() => {
    if (!autoRefresh) return;
    const interval = setInterval(fetchData, 20000);
    return () => clearInterval(interval);
  }, [autoRefresh, fetchData]);

  if (loading) return (
    <div className="flex items-center justify-center h-64">
      <div className="animate-spin rounded-full h-10 w-10 border-b-2 border-orange-500"></div>
    </div>
  );

  return (
    <div className="min-h-screen bg-slate-50">
      {/* Header */}
      <div className="bg-white border-b border-slate-200 px-4 sm:px-6 lg:px-8 py-5">
        <div className="max-w-7xl mx-auto flex items-center justify-between flex-wrap gap-3">
          <div>
            <h1 className="text-2xl font-manrope font-bold text-slate-900 tracking-tight flex items-center gap-2">
              <Wifi className="h-6 w-6 text-orange-500" />
              Suivi des Synchronisations
            </h1>
            <p className="mt-1 text-sm text-slate-500">
              Présences reçues depuis les appareils mobiles BenjScanner Pro
              {lastRefresh && <span className="ml-2 text-slate-400">— actualisé {timeAgo(lastRefresh.toISOString())}</span>}
            </p>
          </div>
          <div className="flex items-center gap-2">
            <button
              onClick={() => setAutoRefresh(a => !a)}
              className={`flex items-center gap-1.5 px-3 py-2 rounded-lg text-sm font-medium border transition-colors ${autoRefresh ? "bg-green-50 text-green-700 border-green-200" : "bg-slate-100 text-slate-600 border-slate-200"}`}
            >
              <Activity className="h-4 w-4" />
              {autoRefresh ? "Live" : "Pausé"}
            </button>
            <button
              onClick={() => { setLoading(true); fetchData(); }}
              className="flex items-center gap-1.5 px-3 py-2 rounded-lg text-sm font-medium bg-orange-500 text-white hover:bg-orange-600 transition-colors shadow-sm"
            >
              <RefreshCw className="h-4 w-4" />
              Actualiser
            </button>
          </div>
        </div>
      </div>

      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-6">

        {/* KPI cards */}
        {stats && (
          <div className="grid grid-cols-2 lg:grid-cols-4 gap-4 mb-6">
            <Card>
              <CardContent className="pt-5">
                <div className="flex items-center justify-between">
                  <div>
                    <p className="text-xs font-medium text-slate-500 uppercase tracking-wide">Total syncs</p>
                    <p className="text-3xl font-bold text-slate-900 mt-1">{stats.total_syncs}</p>
                  </div>
                  <div className="bg-orange-50 p-3 rounded-xl"><Smartphone className="h-6 w-6 text-orange-500" /></div>
                </div>
              </CardContent>
            </Card>
            <Card>
              <CardContent className="pt-5">
                <div className="flex items-center justify-between">
                  <div>
                    <p className="text-xs font-medium text-slate-500 uppercase tracking-wide">Aujourd'hui</p>
                    <p className="text-3xl font-bold text-slate-900 mt-1">{stats.today_syncs}</p>
                  </div>
                  <div className="bg-green-50 p-3 rounded-xl"><Wifi className="h-6 w-6 text-green-500" /></div>
                </div>
              </CardContent>
            </Card>
            <Card>
              <CardContent className="pt-5">
                <div className="flex items-center justify-between">
                  <div>
                    <p className="text-xs font-medium text-slate-500 uppercase tracking-wide">Présences créées</p>
                    <p className="text-3xl font-bold text-slate-900 mt-1">{stats.total_records_created}</p>
                  </div>
                  <div className="bg-blue-50 p-3 rounded-xl"><CheckCircle className="h-6 w-6 text-blue-500" /></div>
                </div>
              </CardContent>
            </Card>
            <Card>
              <CardContent className="pt-5">
                <div className="flex items-center justify-between">
                  <div>
                    <p className="text-xs font-medium text-slate-500 uppercase tracking-wide">Dernière sync</p>
                    <p className="text-sm font-bold text-slate-900 mt-1">{stats.last_sync ? timeAgo(stats.last_sync) : "Jamais"}</p>
                    {stats.last_sync && <p className="text-xs text-slate-400 mt-0.5">{formatTime(stats.last_sync)}</p>}
                  </div>
                  <div className="bg-purple-50 p-3 rounded-xl"><Clock className="h-6 w-6 text-purple-500" /></div>
                </div>
              </CardContent>
            </Card>
          </div>
        )}

        {/* Indicateur live */}
        {autoRefresh && (
          <div className="mb-4 flex items-center gap-2 text-xs text-green-600 font-medium">
            <span className="relative flex h-2 w-2">
              <span className="animate-ping absolute inline-flex h-full w-full rounded-full bg-green-400 opacity-75"></span>
              <span className="relative inline-flex rounded-full h-2 w-2 bg-green-500"></span>
            </span>
            Suivi en temps réel — actualisation toutes les 20 secondes
          </div>
        )}

        {/* Liste des syncs */}
        {logs.length === 0 ? (
          <div className="text-center py-20">
            <div className="mx-auto h-16 w-16 rounded-2xl bg-slate-100 flex items-center justify-center mb-4">
              <Smartphone className="h-8 w-8 text-slate-400" />
            </div>
            <p className="text-slate-600 font-semibold">Aucune synchronisation reçue</p>
            <p className="text-slate-400 text-sm mt-1">
              Les syncs apparaîtront ici dès qu'un appareil mobile enverra des données
            </p>
            <p className="text-slate-400 text-xs mt-3">
              APK : <span className="font-mono text-orange-600">https://scanner.benjinside.site/downloads/benjscanner-pro-v1.0.0.apk</span>
            </p>
          </div>
        ) : (
          <div className="space-y-3">
            <div className="flex items-center justify-between mb-1">
              <p className="text-sm font-semibold text-slate-700">{logs.length} synchronisation{logs.length > 1 ? "s" : ""} reçue{logs.length > 1 ? "s" : ""}</p>
            </div>
            {logs.map((log) => (
              <SyncLogCard key={log.id || log.synced_at} log={log} />
            ))}
          </div>
        )}
      </div>
    </div>
  );
}
